import { useCallback, useEffect, useState } from "react";
import { api } from "./api/client";

/**
 * 화면 하나가 쓰는 데이터를 통째로 불러온다.
 *
 * 화면마다 API가 **한 번**이다(`/api/trips/:id/itinerary`, `/budget`, `/checklist`…).
 * 서버가 조회 여러 개를 `Promise.all`로 묶어 한 왕복에 끝내도록 만들어 뒀기
 * 때문이다 — 클라이언트가 쪼개 부르면 그 이점이 사라진다.
 *
 * `reload`는 `next/navigation` shim의 `router.refresh()`가 부르는 함수다.
 * 컴포넌트 37곳이 저장 후 그걸 호출하고 있어서, 화면이 이걸 등록해두면
 * **컴포넌트를 한 줄도 안 고치고** 갱신이 이어진다.
 */
/**
 * 화면별 마지막 응답. **탭을 오갈 때 기다리지 않으려고 둔다.**
 *
 * 실측(데스크톱): 탭을 누르고 내용이 보일 때까지 230~330ms인데, 그 대부분이
 * 이미 본 데이터를 다시 받는 시간이다. 캐시가 있으면 먼저 그리고 뒤에서
 * 갱신한다(stale-while-revalidate).
 *
 * **기기에도 남긴다**(localStorage). 앱을 껐다 켜도 마지막으로 본 내용이 바로
 * 뜨게 하려는 것이다 — 비행기 모드에서 정산을 다시 열어 보는 상황이 실제로
 * 있다. 개인 기기 전제이고, **사용자가 바뀌거나 로그아웃하면 통째로 지운다**
 * (clearScreenCache — purgeApiCache와 같은 자리에서 부른다).
 *
 * 메모리 맵을 앞에 두는 건 속도 때문이다. 탭을 옮길 때마다 JSON을 다시 파싱할
 * 이유가 없다.
 */
const memo = new Map<string, unknown>();
const KEY_PREFIX = "gotravel:screen:";

function readStored<T>(path: string): T | undefined {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + path);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

function writeStored(path: string, value: unknown): void {
  try {
    localStorage.setItem(KEY_PREFIX + path, JSON.stringify(value));
  } catch {
    // 저장소가 막혔거나 꽉 찼다. 메모리 캐시는 이미 들어가 있으니 그냥 넘어간다.
  }
}

/** 캐시가 있는 경로인가 — 메모리든 기기든. */
function cachedOf<T>(path: string | null): T | undefined {
  if (!path) return undefined;
  if (memo.has(path)) return memo.get(path) as T;
  const stored = readStored<T>(path);
  if (stored !== undefined) memo.set(path, stored);
  return stored;
}

/** 사용자가 바뀌거나 로그아웃할 때 비운다(sw-register의 purgeApiCache와 짝). */
export function clearScreenCache(): void {
  memo.clear();
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(KEY_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // 저장소를 못 읽으면 지울 것도 없다.
  }
}

export type Screen<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
};

export function useScreen<T>(path: string | null): Screen<T> {
  // 첫 렌더부터 캐시를 쓴다. useEffect에서 넣으면 빈 화면이 한 번 그려진다.
  const cached = cachedOf<T>(path);
  const [data, setData] = useState<T | null>(cached ?? null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(cached === undefined);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!path) return;
      try {
        const next = await api.get<T>(path, signal);
        if (signal?.aborted) return;
        memo.set(path, next);
        writeStored(path, next);
        setData(next);
        setError(null);
      } catch (err) {
        if (signal?.aborted) return;
        // **캐시로 이미 그려 놨으면 조용히 넘어간다.** 갱신에 실패했다고
        // 보던 화면을 오류로 덮을 이유가 없다(reload와 같은 규칙).
        if (cachedOf(path) !== undefined) return;
        setError(err instanceof Error ? err.message : "불러오지 못했습니다");
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [path]
  );

  useEffect(() => {
    const ac = new AbortController();
    // 캐시가 있으면 이미 그려 놨다 — 로딩 문구를 띄우지 않는다.
    setLoading(path ? cachedOf(path) === undefined : false);
    void load(ac.signal);
    return () => ac.abort();
  }, [load, path]);

  // 갱신 중에도 **기존 화면을 그대로 둔다** — 저장할 때마다 화면이 빈칸으로
  // 깜빡이면 쓰기 나쁘다. 실패해도 이전 데이터를 지우지 않는다.
  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { data, error, loading, reload };
}
